import React, { useEffect, useState } from "react";
import {
    Card,
    Button,
    Input,
    Typography,
    Row,
    Col,
    Divider,
    Empty,
    Tooltip,
    Tag,
} from "antd";
import {
    DatabaseOutlined,
    FolderOpenOutlined,
    PlayCircleOutlined,
    ReloadOutlined,
    SettingOutlined,
    CloudDownloadOutlined,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import reloadApi from "../../api/b2bDataOperationsApi";
import "./B2BDataOperationPage.css";

const { Title, Text, Paragraph } = Typography;

export default function B2BDataOperationPage() {
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState("");
    const [loadingJobs, setLoadingJobs] = useState(false);
    const [folderPath, setFolderPath] = useState("apollo-backups");

    useEffect(() => {
        loadJobs();
    }, []);

    const loadJobs = async () => {
        try {
            setLoadingJobs(true);

            const res = await reloadApi.getJobs();

            if (res?.success) {
                setJobs(res?.data?.data || []);
            } else {
                toast.error(
                    res?.message || "Unable to load data operations."
                );
            }
        } catch (error) {
            console.error("Load Jobs Error:", error);

            toast.error(
                error?.response?.data?.message ||
                error?.message ||
                "Unable to load data operations."
            );
        } finally {
            setLoadingJobs(false);
        }
    };

    const execute = async (job) => {
        try {
            if (
                job.requiresPath &&
                (!folderPath || folderPath.trim() === "")
            ) {
                toast.error("Please enter a backup folder path.");
                return;
            }

            setLoading(job.key);

            const payload = job.requiresPath
                ? {
                    folderPath: folderPath.trim(),
                }
                : {};

            const res = await reloadApi.executeJob(job.key, payload);

            if (res?.success) {
                toast.success(
                    `${job.label || "Operation"} executed successfully.`
                );
            } else {
                toast.error(
                    res?.message || "Unable to execute the operation."
                );
            }
        } catch (error) {
            console.error("Execute Error:", error);

            toast.error(
                error?.response?.data?.message ||
                error?.message ||
                "Unexpected error occurred while executing the operation."
            );
        } finally {
            setLoading("");
        }
    };

    return (
        <div className="b2b-operation-page">
            <div className="b2b-operation-container">

                {/* ================= HEADER ================= */}
                <div className="b2b-operation-header">

                    <div className="b2b-operation-header-content">

                        <div className="b2b-operation-header-icon">
                            <DatabaseOutlined />
                        </div>

                        <div>
                            <Title
                                level={2}
                                className="b2b-operation-title"
                            >
                                B2B Data Operations
                            </Title>

                            <Paragraph className="b2b-operation-subtitle">
                                Manage and execute data processing operations
                                for the B2B platform.
                            </Paragraph>
                        </div>

                    </div>

                    <Tooltip title="Refresh operations">
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={loadJobs}
                            loading={loadingJobs}
                            className="b2b-refresh-button"
                        >
                            Refresh
                        </Button>
                    </Tooltip>

                </div>

                {/* ================= SUMMARY ================= */}
                <div className="b2b-summary-card">

                    <Row gutter={[24, 16]} align="middle">

                        <Col xs={24} sm={8}>

                            <div className="b2b-summary-item">

                                <div className="b2b-summary-icon">
                                    <SettingOutlined />
                                </div>

                                <div>
                                    <Text className="b2b-summary-label">
                                        Available Operations
                                    </Text>

                                    <div className="b2b-summary-value">
                                        {jobs.length}
                                    </div>
                                </div>

                            </div>

                        </Col>

                        <Col xs={24} sm={16}>

                            <div className="b2b-info-message">

                                <CloudDownloadOutlined className="b2b-info-icon" />

                                <Text className="b2b-info-text">
                                    Select an operation below to start a data
                                    processing task.
                                </Text>

                            </div>

                        </Col>

                    </Row>

                </div>

                {/* ================= SECTION HEADER ================= */}
                <div className="b2b-section-header">

                    <div>
                        <Title
                            level={4}
                            className="b2b-section-title"
                        >
                            Available Operations
                        </Title>

                        <Text type="secondary">
                            Execute supported B2B data management tasks.
                        </Text>
                    </div>

                </div>

                {/* ================= EMPTY STATE ================= */}
                {jobs.length === 0 && !loadingJobs ? (

                    <Card className="b2b-empty-card">

                        <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description={
                                <span>
                                    No data operations are currently
                                    available.
                                </span>
                            }
                        />

                    </Card>

                ) : (

                    /* ================= OPERATIONS ================= */
                    <Row gutter={[20, 20]}>

                        {jobs.map((job) => (

                            <Col
                                xs={24}
                                md={12}
                                key={job.key}
                            >

                                <Card
                                    hoverable
                                    className="b2b-operation-card"
                                    styles={{
                                        body: {
                                            padding: 22,
                                        },
                                    }}
                                >

                                    {/* Operation Header */}
                                    <div className="b2b-operation-card-header">

                                        <div className="b2b-operation-card-icon">
                                            <PlayCircleOutlined />
                                        </div>

                                        <div className="b2b-operation-card-title-area">

                                            <Title
                                                level={5}
                                                className="b2b-operation-card-title"
                                            >
                                                {job.label}
                                            </Title>

                                            <Tag
                                                color="blue"
                                                className="b2b-operation-tag"
                                            >
                                                DATA OPERATION
                                            </Tag>

                                        </div>

                                    </div>

                                    <Divider className="b2b-divider" />

                                    {/* Folder Path */}
                                    {job.requiresPath && (

                                        <div className="b2b-path-section">

                                            <div className="b2b-field-label">

                                                <FolderOpenOutlined className="b2b-field-icon" />

                                                <Text strong>
                                                    Backup Folder
                                                </Text>

                                            </div>

                                            <Text
                                                type="secondary"
                                                className="b2b-field-hint"
                                            >
                                                Specify the folder containing
                                                the required backup data.
                                            </Text>

                                            <Input
                                                size="large"
                                                prefix={
                                                    <FolderOpenOutlined className="b2b-input-icon" />
                                                }
                                                placeholder="Enter backup folder path"
                                                value={folderPath}
                                                onChange={(e) =>
                                                    setFolderPath(
                                                        e.target.value
                                                    )
                                                }
                                                className="b2b-folder-input"
                                                allowClear
                                            />

                                        </div>

                                    )}

                                    {/* Action */}
                                    <div className="b2b-action-area">

                                        <Button
                                            type="primary"
                                            size="large"
                                            icon={<PlayCircleOutlined />}
                                            loading={
                                                loading === job.key
                                            }
                                            onClick={() => execute(job)}
                                            className="b2b-execute-button"
                                        >
                                            {loading === job.key
                                                ? "Executing..."
                                                : "Execute Operation"}
                                        </Button>

                                    </div>

                                </Card>

                            </Col>

                        ))}

                    </Row>

                )}

            </div>
        </div>
    );
}